import React, { FunctionComponent } from 'react';
import EncourageDonateModalContainer from '../EncourageDonateModal/EncourageDonateModalContainer';
import EncourageShareModalContainer from '../EncourageShareModal/EncourageShareModalContainer';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import {
  InactivityModalEventDetailsFragment,
  PageStatus,
} from '../../../types/__generated__';
import gql from 'graphql-tag';
import { usePageOwner } from '../../hooks/usePageOwner';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';

const FRAGMENT = buildQuery(gql`
  fragment InactivityModalEventDetails on Page {
    relationships {
      events(first: 1) {
        totalCount
        nodes {
          startDate
          endDate
          visible
        }
      }
    }
  }
`);

const EncourageInactivityModal: FunctionComponent = () => {
  const { isPageOwner } = usePageOwner();
  const basePageData = useBasePageQuery();
  const result = usePageQuery<InactivityModalEventDetailsFragment>(FRAGMENT);
  const { currentUrl } = useNotNullableContext(PageMetaContext);

  const pageData = basePageData.data?.page;
  const pageIsActive = pageData && pageData.status === PageStatus.Active;
  const isMultiBeneficiaryCampaign =
    pageData?.relationships &&
    pageData?.relationships.beneficiaries.totalCount > 1;
  const hasNoQueryResultData =
    !UsePageQueryResult.hasData(result) || !result.data;
  const isPageUrl = currentUrl?.toLowerCase() === pageData?.url?.toLowerCase();

  if (
    isPageOwner ||
    hasNoQueryResultData ||
    !pageIsActive ||
    isMultiBeneficiaryCampaign ||
    !isPageUrl
  ) {
    return null;
  }

  const isDonateModalRequired = () => {
    if (
      result.data?.relationships?.events.totalCount !== 1 ||
      !result.data.relationships.events.nodes[0]?.visible ||
      !result.data.relationships.events.nodes[0]?.startDate
    ) {
      return false;
    }

    const events = result?.data?.relationships?.events.nodes;
    const event = events && events[0];
    const eventStartDate = new Date(event.startDate);
    const eventEndDate = event.endDate
      ? new Date(event.endDate)
      : eventStartDate;
    const daysVisibleAfterEvent = 3;
    const daysVisibleBeforeEvent = 7;

    const isEventWithinDonateDateRange = () => {
      const now = new Date();

      const earliestEventDateVisible = new Date(eventStartDate);
      earliestEventDateVisible.setDate(
        eventStartDate.getDate() - daysVisibleBeforeEvent,
      );

      const lastEventDateVisible = new Date(eventEndDate);
      lastEventDateVisible.setDate(
        eventEndDate.getDate() + daysVisibleAfterEvent,
      );

      return earliestEventDateVisible <= now && now <= lastEventDateVisible;
    };

    return isEventWithinDonateDateRange();
  };

  const showDonateModal = isDonateModalRequired();

  return (
    <>
      {showDonateModal && <EncourageDonateModalContainer />}
      {!showDonateModal && <EncourageShareModalContainer />}
    </>
  );
};

export default EncourageInactivityModal;
